import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  FlatList,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { Image } from 'expo-image';
import { Feather, Ionicons } from '@expo/vector-icons';
import { useColors, ThemeColors } from '../../hooks/useColors';
import { useVendorRatings, VendorRating } from '../../hooks/useVendorRatings';
import { spacing, typography } from '../../theme';
import { getWebImageUrl } from '../../utils/format';

const PHONE_CONTAINER_MAX_WIDTH = 430;

interface VendorRatingsModalProps {
  visible: boolean;
  onClose: () => void;
  vendorId: string | undefined;
  storeName?: string;
}

export function VendorRatingsModal({
  visible,
  onClose,
  vendorId,
  storeName,
}: VendorRatingsModalProps) {
  const colors = useColors();
  const styles = createStyles(colors);
  const { data, isLoading } = useVendorRatings(vendorId);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const renderStars = (rating: number) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Ionicons
          key={i}
          name={i <= rating ? 'star' : 'star-outline'}
          size={14}
          color={i <= rating ? colors.primary : colors.textLight}
        />
      );
    }
    return <View style={styles.starsContainer}>{stars}</View>;
  };

  const renderRatingItem = ({ item }: { item: VendorRating }) => {
    // Transform image URL for web compatibility (HEIC -> JPEG)
    const productImageUrl = Platform.OS === 'web' 
      ? getWebImageUrl(item.product.images?.[0]) 
      : item.product.images?.[0];

    return (
    <View style={styles.ratingItem}>
      <View style={styles.ratingHeader}>
        <View style={styles.ratingLeft}>
          {productImageUrl ? (
            <Image
              source={{ uri: productImageUrl }}
              style={styles.productImage}
              contentFit="cover"
            />
          ) : (
            <View style={[styles.productImage, styles.productImagePlaceholder]}>
              <Feather name="image" size={16} color={colors.textLight} />
            </View>
          )}
          <View style={styles.ratingInfo}>
            <Text style={styles.productTitle} numberOfLines={1}>
              {item.product.title}
            </Text>
                <Text style={styles.username}>
                  {item.buyer.username 
                    ? `@${item.buyer.username}` 
                    : item.buyer.full_name 
                      ? item.buyer.full_name 
                      : 'Verified Buyer'}
                </Text>
          </View>
        </View>
        <View style={styles.ratingRight}>
          {renderStars(item.rating)}
          <Text style={styles.saleDate}>
            {formatDate(item.sale_date || item.created_at)}
          </Text>
        </View>
      </View>
      {item.feedback && (
        <Text style={styles.feedback}>"{item.feedback}"</Text>
      )}
    </View>
  );
  };

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <Ionicons name="star-outline" size={48} color={colors.textLight} />
      <Text style={styles.emptyTitle}>No ratings yet</Text>
      <Text style={styles.emptyText}>
        Ratings will appear here after customers review purchases.
      </Text>
    </View>
  );

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={true}
      onRequestClose={onClose}
    >
      <View style={styles.webModalWrapper}>
        <View style={styles.overlay}>
          <View style={styles.content}>
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <Text style={styles.title}>Ratings & Reviews</Text>
              {storeName && (
                <Text style={styles.subtitle}>{storeName}</Text>
              )}
            </View>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Feather name="x" size={24} color={colors.text} />
            </TouchableOpacity>
          </View>

          {data && data.totalRatings > 0 && (
            <View style={styles.summary}>
              <View style={styles.averageRating}>
                <Ionicons name="star-outline" size={24} color={colors.primary} />
                <Text style={styles.averageText}>
                  {data.averageRating?.toFixed(1) || 'N/A'}
                </Text>
              </View>
              <Text style={styles.totalRatings}>
                {data.totalRatings} {data.totalRatings === 1 ? 'rating' : 'ratings'}
              </Text>
            </View>
          )}

          {isLoading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={colors.primary} />
            </View>
          ) : (
            <FlatList
              data={data?.ratings || []}
              renderItem={renderRatingItem}
              keyExtractor={(item) => item.id}
              contentContainerStyle={styles.listContent}
              ListEmptyComponent={renderEmptyState}
              showsVerticalScrollIndicator={false}
            />
          )}
          </View>
        </View>
      </View>
    </Modal>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    webModalWrapper: {
      flex: 1,
      ...(Platform.OS === 'web' ? {
        maxWidth: PHONE_CONTAINER_MAX_WIDTH,
        alignSelf: 'center',
        width: '100%',
      } : {}),
    },
    overlay: {
      flex: 1,
      backgroundColor: colors.overlay,
      justifyContent: 'flex-end',
    },
    content: {
      backgroundColor: colors.surface,
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      maxHeight: '85%',
      minHeight: '50%',
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      padding: spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    headerLeft: {
      flex: 1,
    },
    title: {
      ...typography.title,
      fontSize: 20,
      fontWeight: '600',
      color: colors.text,
    },
    subtitle: {
      ...typography.body,
      fontSize: 14,
      color: colors.textLight,
      marginTop: 2,
    },
    closeButton: {
      padding: spacing.xs,
    },
    summary: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      backgroundColor: colors.backgroundSecondary,
    },
    averageRating: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    averageText: {
      fontSize: 24,
      fontWeight: '700',
      color: colors.text,
    },
    totalRatings: {
      ...typography.body,
      color: colors.textLight,
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: spacing.xl,
    },
    listContent: {
      padding: spacing.md,
      flexGrow: 1,
    },
    ratingItem: {
      backgroundColor: colors.background,
      borderRadius: 12,
      padding: spacing.md,
      marginBottom: spacing.sm,
      borderWidth: 1,
      borderColor: colors.border,
    },
    ratingHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    },
    ratingLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
    },
    productImage: {
      width: 44,
      height: 44,
      borderRadius: 8,
      backgroundColor: colors.backgroundSecondary,
    },
    productImagePlaceholder: {
      justifyContent: 'center',
      alignItems: 'center',
    },
    ratingInfo: {
      marginLeft: spacing.sm,
      flex: 1,
    },
    productTitle: {
      ...typography.body,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 2,
    },
    username: {
      ...typography.caption,
      color: colors.primary,
      fontWeight: '500',
    },
    ratingRight: {
      alignItems: 'flex-end',
    },
    starsContainer: {
      flexDirection: 'row',
      gap: 2,
      marginBottom: 4,
    },
    saleDate: {
      ...typography.caption,
      color: colors.textLight,
      fontSize: 11,
    },
    feedback: {
      ...typography.body,
      color: colors.textLight,
      fontStyle: 'italic',
      marginTop: spacing.sm,
      paddingTop: spacing.sm,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    emptyState: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: spacing.xl,
    },
    emptyTitle: {
      ...typography.title,
      fontSize: 18,
      color: colors.text,
      marginTop: spacing.md,
    },
    emptyText: {
      ...typography.body,
      color: colors.textLight,
      textAlign: 'center',
      marginTop: spacing.xs,
    },
  });

